import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { api } from '../../services/api';
import { useToast } from '../ui/Toast';

interface DeleteUserModalProps {
  user: { id: string; name: string; email: string } | null;
  onClose: () => void;
  /** Called after a successful delete (e.g. refresh the list or leave the page). */
  onDeleted: () => void | Promise<void>;
}

/**
 * Permanent delete with a typed confirmation: the admin must type the user's
 * email (or name, if there's no email) before the button unlocks.
 */
export function DeleteUserModal({ user, onClose, onDeleted }: DeleteUserModalProps) {
  const { toast } = useToast();
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const expected = (user?.email || user?.name || '').trim();
  const confirmed = expected !== '' && confirmText.trim().toLowerCase() === expected.toLowerCase();

  const close = () => {
    if (deleting) return;
    setConfirmText('');
    onClose();
  };

  const handleDelete = async () => {
    if (!user || !confirmed) return;
    setDeleting(true);
    try {
      const res = await api.deleteUser(user.id);
      toast(
        `${user.name} deleted (${res.deletedTwins} twin${res.deletedTwins !== 1 ? 's' : ''}, ` +
          `${res.deletedUserChats} chat${res.deletedUserChats !== 1 ? 's' : ''} removed)`,
        'success',
      );
      setConfirmText('');
      await onDeleted();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete user.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Modal
      isOpen={Boolean(user)}
      onClose={close}
      title="Delete user permanently"
      size="sm"
      closeOnBackdropClick={!deleting}
      allowDismiss={!deleting}
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void handleDelete()} loading={deleting} disabled={!confirmed}>
            Delete user
          </Button>
        </>
      }
    >
      {user ? (
        <div className="space-y-4">
          <p className="text-[#94a3b8]">
            This permanently deletes <span className="text-[#f8fafc] font-medium">{user.name}</span> and
            everything linked to them. <span className="text-[#fca5a5]">This can't be undone.</span>
          </p>
          <ul className="list-disc pl-5 space-y-1 text-sm text-[#94a3b8]">
            <li>Their login - they can no longer sign in</li>
            <li>Profile, credits and payment history</li>
            <li>All their twins</li>
            <li>Their match chats and twin-to-twin chats - the other person loses these chats too</li>
          </ul>
          <div>
            <label className="block text-xs font-semibold text-[#94a3b8] mb-1.5">
              Type <span className="text-[#f8fafc] font-mono">{expected}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={deleting}
              autoComplete="off"
              className="w-full bg-[#0f0f1a] border border-[#252535] rounded-xl px-3 py-2.5 text-sm text-[#f1f5f9] focus:outline-none focus:border-[#ef4444]"
            />
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
